/**
 * The AI tab — README "4. Context + AI pane (pane C)": a warn-subtle
 * "NOT WIRED · SEAM ONLY" card, three dashed placeholder suggestions, and a
 * priority-read note. Purely presentational: every piece of `aiRead` it
 * renders was fetched by the parent (ContextPane) through ./aiOps.ts's
 * ops-shaped read, never computed or hardcoded in this component and never
 * read off the console store. This component itself never calls an
 * operation — see task-16-report.md for how "sends no request" and "reads
 * from the ops layer" are both true at once.
 */
import { AI_SEAM_BLURB, AI_SEAM_TITLE, AI_UNAVAILABLE_TITLE, PRIORITY_CLASS, PRIORITY_LABEL } from './format'
import type { AiPaneRead } from './aiOps'

export interface AiTabProps {
  aiRead: AiPaneRead
  /**
   * Whether this conversation is having its hints read. Undefined = on.
   *
   * The switch itself lives beside the AI HINTS heading in ContextPane rather
   * than in here: it governs the whole section, so it belongs on the section's
   * own title row, not stacked inside the content it turns off.
   */
  enabled?: boolean
}

export function AiTab({ aiRead, enabled = true }: AiTabProps) {
  return (
    <div className="flex flex-col gap-4" data-testid="ai-tab">
      {/* Three states, deliberately distinct. Wired shows no banner at all —
          a permanent warning card above working content trains people to
          ignore warnings. `unavailable` names the actual cause. The seam is
          only for a route that did not answer, which now means an outage or
          an old console build, not "unbuilt". */}
      {!enabled ? (
        <p data-testid="ai-hints-off" className="text-[11px] leading-[1.55] text-lo">
          Hints are off for this conversation, so nothing is being read or paid for. Turning them back on
          reads it fresh.
        </p>
      ) : aiRead.idle ? (
        // Fix round 2 (finding 12): was `bg-elevated` — an inset panel,
        // structurally identical to TicketSection.tsx's no-ticket card
        // (already `bg-sunken`).
        <p data-testid="ai-idle" className="rounded-[10px] bg-sunken p-3 text-[11px] leading-[1.55] text-lo">
          Open a conversation and hints will appear here.
        </p>
      ) : aiRead.loading ? (
        /* A read in flight is not the seam. Saying "appears once the AI
           operator is wired" while it is thinking told everyone the feature
           did not exist. */
        <p data-testid="ai-loading" className="text-[11px] text-lo">
          Reading the conversation…
        </p>
      ) : aiRead.wired ? null : aiRead.unavailable ? (
        <div className="rounded-[10px] bg-warn-subtle p-3" data-testid="ai-unavailable-card">
          <div className="mb-1.5 text-[10px] font-semibold tracking-[.08em] text-warn">{AI_UNAVAILABLE_TITLE}</div>
          <div className="text-[11px] leading-[1.55] text-mid">{aiRead.unavailable}</div>
        </div>
      ) : (
        <div className="rounded-[10px] bg-warn-subtle p-3" data-testid="ai-seam-card">
          <div className="mb-1.5 text-[10px] font-semibold tracking-[.08em] text-warn">{AI_SEAM_TITLE}</div>
          <div className="text-[11px] leading-[1.55] text-mid">{AI_SEAM_BLURB}</div>
        </div>
      )}

      {aiRead.priority && enabled ? (
        <div>
          <div className="mb-1.5 text-[10px] font-semibold tracking-[.08em] text-lo">PRIORITY</div>
          {/* A chip, not a paragraph. The old copy explained a judgement at
              length that nobody could act on at a glance, and used our own
              vocabulary rather than the one on the ticket. */}
          <span
            data-testid="ai-priority-chip"
            className={[
              'inline-block rounded-[20px] px-2.5 py-1 text-[11px] font-semibold',
              PRIORITY_CLASS[aiRead.priority],
            ].join(' ')}
          >
            {/* README §5's HINTS block: "'Priority: Medium' pill." */}
            Priority: {PRIORITY_LABEL[aiRead.priority]}
          </span>
        </div>
      ) : null}
      {/* Not rendered rather than hidden. A hidden block is still in the
          document, and once somebody has switched the reading off to stop
          paying for it, leaving the last set of suggestions sitting in the
          page is exactly the thing they asked us to stop doing. */}
      {!aiRead.loading && !aiRead.idle && enabled ? (
        <div>
          <div className="mb-1.5 text-[10px] font-semibold tracking-[.08em] text-lo">NEXT QUESTIONS</div>
          <div className="flex flex-col gap-1.5" data-testid="ai-suggestions">
            {aiRead.suggestions.map((s) => (
              <div
                key={s.id}
                data-testid={`ai-suggestion-${s.id}`}
                className={
                  // README §5: "suggestions as `--surface-sunken` cards."
                  aiRead.wired
                    ? 'rounded-[8px] border border-subtle bg-sunken px-[10px] py-[9px] text-[11px] leading-[1.45] text-mid'
                    : 'rounded-[8px] border border-dashed border-subtle bg-sunken px-[10px] py-[9px] text-[11px] leading-[1.45] text-lo'
                }
              >
                {s.text}
              </div>
            ))}
          </div>
        </div>
      ) : null}

    </div>
  )
}
